import { z } from 'zod';
import { ROLES, membershipStatusSchema, roleSchema } from './role';

// Owners are neither invited nor demoted here; transfer is a later feature.
export const INVITABLE_ROLES = ROLES.filter((role) => role !== 'owner');

export const invitableRoleSchema = roleSchema.refine(
  (role) => role !== 'owner',
  'owner cannot be invited or assigned; see TODOS.md',
);

// What the caller may do in the venue they are working in, checked per request.
export const myMembershipSchema = z.object({
  venueId: z.uuid(),
  role: roleSchema,
  status: membershipStatusSchema,
  viaPlatform: z.boolean(),
});

export type MyMembership = z.infer<typeof myMembershipSchema>;

export type InvitableRole = z.infer<typeof invitableRoleSchema>;

export const teamMemberSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  name: z.string().nullable(),
  email: z.string(),
  role: roleSchema,
  status: membershipStatusSchema,
  invitedAt: z.string().nullable(),
  joinedAt: z.string().nullable(),
});

export type TeamMember = z.infer<typeof teamMemberSchema>;

// An invitee is named by their address until they tell us their own name.
export function memberName(member: Pick<TeamMember, 'name' | 'email'>): string {
  return member.name ?? member.email;
}

export const teamResponseSchema = z.object({ members: z.array(teamMemberSchema) });

export type TeamResponse = z.infer<typeof teamResponseSchema>;

export const inviteRequestSchema = z.object({
  name: z.string().trim().min(1),
  email: z.email(),
  role: invitableRoleSchema,
});

export type InviteRequest = z.infer<typeof inviteRequestSchema>;

// Reactivating an invitee re-invites; §4 locks the address once accepted.
export const memberUpdateSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    email: z.email().optional(),
    role: invitableRoleSchema.optional(),
    active: z.boolean().optional(),
  })
  .refine((u) => Object.values(u).some((value) => value !== undefined), 'nothing to change');

export type MemberUpdate = z.infer<typeof memberUpdateSchema>;
